<script setup lang="ts">
/**
 * PublicShareSheet — LA fenêtre de partage des pages publiques (cagnotte,
 * billetterie, boutique…). Karim 2026-09-25 : « ça doit être la même partout,
 * avec les ronds pour chaque réseau ». Née de l'ancienne fenêtre de la
 * cagnotte ; les pages ne l'appellent pas directement mais via leur wrapper
 * (CampaignShareBottomSheet, EventShareModal, BoutiqueShareModal), qui ne
 * fournit QUE les textes propres à son type de page.
 */

const { t } = useI18n();
const props = defineProps<{
  open: boolean;
  /** Titre de la fenêtre, ex. « Partager la cagnotte ». */
  title: string;
  /** Nom de l'élément partagé (sous le titre). */
  itemName: string;
  /** URL publique complète. */
  url: string;
  /** Accroche des messages (WhatsApp, X, Telegram, SMS), sans l'URL. */
  message: string;
  emailSubject: string;
  /** Corps de l'e-mail, URL comprise. */
  emailBody: string;
  /** Sous le QR code, ex. « Scannez pour accéder à la cagnotte ». */
  qrCaption: string;
  /** Affiche imprimée : phrase sous le titre et mot de la fin. */
  posterSubtitle: string;
  posterCta: string;
  /** Nom du fichier PNG du QR code. */
  fileName: string;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const publicUrl = computed(() => props.url);

const isClosing = ref(false);
const isCopied = ref(false);

function closeModal() {
  isClosing.value = true;
  setTimeout(() => {
    emit("update:open", false);
    isClosing.value = false;
    showQRCode.value = false;
  }, 200);
}

function handleOverlayClick() {
  closeModal();
}

async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(publicUrl.value);
    isCopied.value = true;
    setTimeout(() => { isCopied.value = false; }, 2500);
  } catch {
    // Fallback silencieux
  }
}

function shareOnWhatsApp() {
  const text = encodeURIComponent(`${props.message} ${publicUrl.value}`);
  window.open(`https://wa.me/?text=${text}`, "_blank");
  closeModal();
}

function shareOnFacebook() {
  const url = encodeURIComponent(publicUrl.value);
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  closeModal();
}

function shareOnTwitter() {
  const text = encodeURIComponent(props.message);
  const url = encodeURIComponent(publicUrl.value);
  window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  closeModal();
}

function shareOnTelegram() {
  const text = encodeURIComponent(props.message);
  window.open(`https://t.me/share/url?url=${encodeURIComponent(publicUrl.value)}&text=${text}`, "_blank");
  closeModal();
}

function shareByEmail() {
  const subject = encodeURIComponent(props.emailSubject);
  const body = encodeURIComponent(props.emailBody);
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
  closeModal();
}

function shareOnMessenger() {
  const url = encodeURIComponent(publicUrl.value);
  window.open(`https://www.facebook.com/dialog/send?link=${url}&app_id=966242223397117&redirect_uri=${encodeURIComponent(window.location.href)}`, "_blank");
  closeModal();
}

function shareBySMS() {
  const text = encodeURIComponent(`${props.message} ${publicUrl.value}`);
  const separator = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "&" : "?";
  window.location.href = `sms:${separator}body=${text}`;
  closeModal();
}

// QR Code
const showQRCode = ref(false);
const qrCodeRef = ref<HTMLDivElement | null>(null);
// Reason: qr-code-styling does not export proper TS types for instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const qrCodeInstance = ref<any>(null);

function openQRCodeModal() {
  showQRCode.value = true;
}

async function generateQRCode() {
  if (!qrCodeRef.value) return;
  qrCodeRef.value.innerHTML = "";

  const QRCodeStyling = (await import("qr-code-styling")).default;
  qrCodeInstance.value = new QRCodeStyling({
    width: 400,
    height: 400,
    data: publicUrl.value,
    dotsOptions: { color: "#1f2937", type: "rounded" },
    backgroundOptions: { color: "#ffffff" },
    cornersSquareOptions: { type: "extra-rounded" },
    cornersDotOptions: { type: "dot" },
    imageOptions: { crossOrigin: "anonymous", margin: 10 },
  });
  qrCodeInstance.value.append(qrCodeRef.value);
}

watch(qrCodeRef, (newRef) => {
  if (newRef && showQRCode.value) {
    generateQRCode();
  }
});

function goBackToShare() {
  showQRCode.value = false;
}

async function downloadQRCode() {
  if (!qrCodeInstance.value) return;
  qrCodeInstance.value.download({
    name: props.fileName,
    extension: "png",
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

async function printPoster() {
  if (!qrCodeInstance.value) return;
  const qrDataUrl = await qrCodeInstance.value.getRawData("png");
  if (!qrDataUrl || !(qrDataUrl instanceof Blob)) return;

  const base64 = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(qrDataUrl);
  });

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  // Échappé : le nom vient de l'association et part dans du HTML brut.
  const itemTitle = escapeHtml(props.itemName);

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Affiche - ${itemTitle}</title>
      <style>
        @page { size: A4; margin: 2cm; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; padding: 2rem; text-align: center; }
        h1 { font-size: 2.5rem; margin-bottom: 1rem; color: #1f2937; }
        p { font-size: 1.25rem; color: #6b7280; margin-bottom: 2rem; }
        .qr-container { padding: 1.5rem; background: white; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
        .qr-container img { width: 300px; height: 300px; }
        .url { margin-top: 2rem; font-size: 1rem; color: #9ca3af; word-break: break-all; }
        .cta { margin-top: 2rem; font-size: 1.5rem; font-weight: 600; color: #1f2937; }
      </style>
    </head>
    <body>
      <h1>${itemTitle}</h1>
      <p>${escapeHtml(props.posterSubtitle)}</p>
      <div class="qr-container"><img src="${base64}" alt=t("partage.feuillePartage.qrCode") /></div>
      <p class="url">${escapeHtml(publicUrl.value)}</p>
      <p class="cta">${escapeHtml(props.posterCta)}</p>
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { printWindow.print(); }, 250);
}

const shareOptions = computed(() => [
  { id: "whatsapp", label: "WhatsApp", icon: "i-simple-icons-whatsapp", color: "bg-[#25D366]", action: shareOnWhatsApp },
  { id: "facebook", label: "Facebook", icon: "i-lucide-facebook", color: "bg-[#1877F2]", action: shareOnFacebook },
  { id: "twitter", label: "X", icon: "i-lucide-twitter", color: "bg-black", action: shareOnTwitter },
  { id: "telegram", label: "Telegram", icon: "i-lucide-send", color: "bg-[#0088cc]", action: shareOnTelegram },
  { id: "messenger", label: "Messenger", icon: "i-simple-icons-messenger", color: "bg-gradient-to-br from-[#00B2FF] via-[#006AFF] to-[#A033FF]", action: shareOnMessenger },
  { id: "sms", label: "SMS", icon: "i-lucide-smartphone", color: "bg-[#34C759]", action: shareBySMS },
  { id: "email", label: t("association.publicPage.buyerForm.emailLabel"), icon: "i-lucide-mail", color: "bg-gray-600", action: shareByEmail },
  { id: "qrcode", label: t("partage.feuillePartage.qrCode"), icon: "i-lucide-qr-code", color: "bg-gray-900 dark:bg-gray-700", action: openQRCodeModal },
]);
</script>

<template>
  <Teleport to="body">
    <Transition name="share-modal">
      <div v-if="open" class="share-modal-wrapper" :class="{ 'is-closing': isClosing }">
        <div class="share-modal-overlay" @click="handleOverlayClick" />
        <div class="share-modal-content">
          <Transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 translate-x-4"
            enter-to-class="opacity-100 translate-x-0"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 -translate-x-4"
            mode="out-in"
          >
            <!-- Page 1: Share options -->
            <div v-if="!showQRCode" key="share" class="p-6 pb-8">
              <div class="flex justify-center mb-4 sm:hidden">
                <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>
              <button
                class="hidden sm:flex absolute top-4 right-4 w-8 h-8 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
                @click="closeModal"
              >
                <UIcon name="i-lucide-x" class="size-5" />
              </button>
              <div class="text-center mb-6">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-1">{{ title }}</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{{ itemName }}</p>
              </div>
              <!-- Copier le lien — IDENTIQUE à la fenêtre de partage du
                   dashboard (ShareBottomSheet) : pilule, rond bleu Donoor,
                   icône copier → coche à droite (Karim 2026-09-25). -->
              <button
                class="w-full flex items-center gap-3 py-2.5 pl-2.5 pr-5 mb-6 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                :class="{ 'bg-main/10! dark:bg-main/25!': isCopied }"
                @click="copyToClipboard"
              >
                <div class="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-main">
                  <UIcon
                    :name="isCopied ? 'i-lucide-check' : 'i-lucide-link'"
                    class="size-4 text-white transition-transform duration-300"
                    :class="{ 'scale-110': isCopied }"
                  />
                </div>
                <div class="flex-1 text-left min-w-0">
                  <p class="text-sm font-medium transition-colors duration-300" :class="isCopied ? 'text-main dark:text-blue-300' : 'text-gray-900 dark:text-white'">
                    {{ isCopied ? $t("partage.feuillePartage.lienCopie") : $t("partage.feuillePartage.copierLien") }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ publicUrl }}</p>
                </div>
                <div class="shrink-0 relative w-5 h-5">
                  <Transition name="icon-swap">
                    <UIcon v-if="isCopied" key="check" name="i-lucide-check" class="size-5 text-main dark:text-blue-300 absolute inset-0" />
                    <UIcon v-else key="copy" name="i-lucide-copy" class="size-5 text-gray-400 absolute inset-0" />
                  </Transition>
                </div>
              </button>
              <div class="grid grid-cols-4 gap-3">
                <button
                  v-for="option in shareOptions"
                  :key="option.id"
                  class="flex flex-col items-center gap-1.5 group"
                  @click="option.action"
                >
                  <div :class="[option.color, 'w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform']">
                    <UIcon :name="option.icon" class="size-5" />
                  </div>
                  <span class="text-[11px] text-gray-600 dark:text-gray-400 font-medium">{{ option.label }}</span>
                </button>
              </div>
              <button
                class="w-full mt-6 py-3 text-center text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors sm:hidden"
                @click="closeModal"
              >
                {{ $t("association.publicPage.orderErrorBanner.fermer") }}
              </button>
            </div>

            <!-- Page 2: QR Code -->
            <div v-else key="qrcode" class="p-6 py-4">
              <div class="flex justify-center mb-4 sm:hidden">
                <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>
              <div class="flex items-center justify-between mb-4">
                <button
                  class="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  @click="goBackToShare"
                >
                  <UIcon name="i-lucide-arrow-left" class="size-5" />
                </button>
                <div class="text-center flex-1">
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ $t("partage.feuillePartage.qrCode") }}</h3>
                </div>
                <button
                  class="w-9 h-9 hidden sm:flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  @click="closeModal"
                >
                  <UIcon name="i-lucide-x" class="size-5" />
                </button>
                <div class="w-9 h-9 sm:hidden" />
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                {{ qrCaption }}
              </p>
              <div class="flex justify-center">
                <div ref="qrCodeRef" class="bg-white p-2 rounded-lg qr-code-container" />
              </div>
              <div class="mt-5 space-y-2">
                <button
                  class="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-full text-sm bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                  @click="printPoster"
                >
                  <UIcon name="i-lucide-printer" class="size-4" />
                  {{ $t("partage.feuillePartage.imprimerAffiche") }}
                </button>
                <button
                  class="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-full text-sm bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  @click="downloadQRCode"
                >
                  <UIcon name="i-lucide-download" class="size-4" />
                  {{ $t("partage.feuillePartage.enregistrerImage") }}
                </button>
              </div>
              <button
                class="w-full mt-4 py-3 text-center text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors sm:hidden"
                @click="goBackToShare"
              >
                {{ $t("commun.actions.retour") }}
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.share-modal-wrapper { position: fixed; inset: 0; z-index: 100; display: flex; align-items: flex-end; justify-content: center; }
@media (min-width: 640px) { .share-modal-wrapper { align-items: center; } }
.share-modal-overlay { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); animation: fadeIn 200ms ease-out; }
.is-closing .share-modal-overlay { animation: fadeOut 200ms ease-in forwards; }
.share-modal-content { position: relative; width: 100%; max-height: 85vh; overflow: hidden; background: white; border-radius: 2rem 2rem 0 0; animation: slideUp 300ms cubic-bezier(0.32, 0.72, 0, 1); }
.share-modal-content > :deep(div) { max-height: 85vh; overflow-y: auto; overflow-x: hidden; }
.is-closing .share-modal-content { animation: slideDown 200ms cubic-bezier(0.32, 0.72, 0, 1) forwards; }
@media (min-width: 640px) {
  .share-modal-content { width: 28rem; max-width: calc(100vw - 2rem); border-radius: 1.5rem; animation: scaleIn 200ms ease-out; }
  .is-closing .share-modal-content { animation: scaleOut 150ms ease-in forwards; }
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes slideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
@keyframes scaleOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.95); } }
.share-modal-leave-active { transition: opacity 200ms ease-in; }
.share-modal-leave-to { opacity: 0; }
.icon-swap-enter-active { animation: icon-pop-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.icon-swap-leave-active { animation: icon-pop-out 0.2s ease-in forwards; }
@keyframes icon-pop-in { 0% { opacity: 0; transform: scale(0.5) rotate(-10deg); } 100% { opacity: 1; transform: scale(1) rotate(0deg); } }
@keyframes icon-pop-out { 0% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(0.5); } }
.qr-code-container :deep(canvas),
.qr-code-container :deep(svg) { width: 180px !important; height: 180px !important; }
</style>
